/**
 * DashboardUI — primitivos visuais internos do Dashboard.
 *
 * Exports:
 *   ActivityRings    — gráfico de três anéis de progresso
 *   DashboardSkeleton — placeholder de loading
 *   Card             — wrapper simples com estilo de card
 *   PointsCard       — card de pontos com tier/level
 *   ProgressBar      — barra de progresso genérica
 *   Modal            — modal reutilizável com header/footer
 */
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFireFlameCurved, faArrowTrendUp } from "@fortawesome/free-solid-svg-icons";
import { getUserLevel } from "../utils/levels";
import ModalHeader from "./ModalHeader";
import styles from "../pages/Dashboard.module.css";
import { themeColors } from "../utils/theme";

const cn = (...classes) => classes.filter(Boolean).join(" ");

// ── ActivityRings ─────────────────────────────────────────────────────────────
export function ActivityRings({
  weeklyProgress = 0,
  dailyProgress = 0,
  challengeProgress = 0,
  weeklyCheckins = 0,
  weeklyGoal = 3,
  todayCheckins = 0,
  size = 180,
  // compact: versão do mobile (achado P2 da auditoria de 06/09/2026 — o
  // visual mais motivacional do app era desktop-only, justamente onde o uso
  // NÃO acontece). Some com a legenda lateral e troca o centro pro número
  // da meta semanal, que é o que puxa a decisão de treinar hoje
  // (Goal Gradient).
  compact = false,
}) {
  const cx = size / 2;
  // Espessura e respiro proporcionais ao tamanho: com tw fixo em 13, um
  // anel pequeno (~90px) colapsava o 3º círculo em raio quase zero. Nos
  // 180px originais os valores caem exatamente em 13 e 5, então o desktop
  // continua pixel a pixel como era.
  const tw = Math.max(6, Math.round(size * 0.0722));
  const gap = Math.max(3, Math.round(size * 0.0278));
  const rings = [
    { r: cx - tw / 2 - 1,                  pct: weeklyProgress,    color: themeColors.blue, track: `rgba(${themeColors.accentRgb},0.20)`,  label: `${weeklyCheckins}/${weeklyGoal}`,    caption: "esta semana" },
    { r: cx - tw / 2 - tw - gap - 1,       pct: dailyProgress,     color: "var(--amber)",             track: "rgba(239,159,39,0.20)",  label: `${todayCheckins}/2`,                 caption: "hoje"        },
    { r: cx - tw / 2 - (tw + gap) * 2 - 1, pct: challengeProgress, color: "#9f77dd",            track: "rgba(159,119,221,0.20)", label: `${Math.round(challengeProgress)}%`,  caption: "desafio"     },
  ];

  const calcDash = (r, pct) => {
    const c   = 2 * Math.PI * r;
    const off = c * (1 - Math.max(0, Math.min(100, pct)) / 100);
    return { strokeDasharray: String(c), strokeDashoffset: String(off) };
  };

  return (
    <div className={styles.activityRingsWrap}>
      <div className={styles.activityRingsSvgWrap} style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          style={{ transform: "rotate(-90deg)", overflow: "visible" }}
        >
          {rings.map((ring, i) => {
            const d = calcDash(ring.r, ring.pct);
            return (
              <g key={i}>
                <circle cx={cx} cy={cx} r={ring.r} fill="none" stroke={ring.track} strokeWidth={tw} />
                <circle
                  cx={cx} cy={cx} r={ring.r} fill="none"
                  stroke={ring.color} strokeWidth={tw}
                  strokeLinecap="round"
                  strokeDasharray={d.strokeDasharray}
                  strokeDashoffset={d.strokeDashoffset}
                  style={{ transition: "stroke-dashoffset 1.1s ease" }}
                />
              </g>
            );
          })}
        </svg>
        <div className={styles.activityRingsCenter}>
          <span className={styles.activityRingsCenterNum}>
            {compact ? `${weeklyCheckins}/${weeklyGoal}` : `${todayCheckins}/2`}
          </span>
          <span className={styles.activityRingsCenterLabel}>
            {compact ? "semana" : "hoje"}
          </span>
        </div>
      </div>
      {!compact && (
        <div className={styles.activityRingsLegend}>
          {rings.map((ring, i) => (
            <div key={i} className={styles.activityRingLegendRow}>
              <span className={styles.activityRingDot} style={{ background: ring.color }} />
              <span className={styles.activityRingLegendLabel}>{ring.label}</span>
              <span className={styles.activityRingLegendCaption}>{ring.caption}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── DashboardSkeleton ─────────────────────────────────────────────────────────
export function DashboardSkeleton({ isMobile }) {
  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.skeletonStack}>
          <div className={cn(styles.skeletonBlock, styles.skeletonAvatar)} />
          <div className={cn(styles.skeletonBlock, styles.skeletonLineLg)} />
        </div>
        <div className={cn(styles.skeletonBlock, styles.skeletonBtn)} />
      </div>

      <div className={styles.skeletonSection}>
        <div className={cn(styles.skeletonBlock, styles.skeletonHero)} />
        <div className={cn(styles.skeletonBlock, styles.skeletonRank)} />
      </div>

      {!isMobile && (
        <div className={styles.skeletonMetrics}>
          {[...Array(4)].map((_, idx) => (
            <div key={`metric-${idx}`} className={cn(styles.skeletonBlock, styles.skeletonMetric)} />
          ))}
        </div>
      )}

      <div className={styles.challengeGrid}>
        {[...Array(3)].map((_, idx) => (
          <div key={`challenge-${idx}`} className={cn(styles.card, styles.skeletonCard)} />
        ))}
      </div>

      <div className={cn(styles.card, styles.skeletonTimeline)} />
    </div>
  );
}

// ── Card ──────────────────────────────────────────────────────────────────────
export function Card({ children }) {
  return <div className={styles.card}>{children}</div>;
}

// ── PointsCard ────────────────────────────────────────────────────────────────
export function PointsCard({ total, today, onClick }) {
  const level = getUserLevel(total);
  return (
    <div
      className={styles.pointsCard}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className={styles.pointsTop} style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <FontAwesomeIcon icon={faFireFlameCurved} />
        Seus Pontos
        <span className={styles.pointsLevelBadge}><FontAwesomeIcon icon={level.icon} /> {level.tier}</span>
      </div>
      <div className={styles.pointsValue}>{total} pts</div>
      {level.next && (
        <>
          <div className={styles.pointsLevelBar}>
            <div className={styles.pointsLevelBarFill} style={{ width: `${level.progress}%` }} />
          </div>
          <small style={{ fontSize: 11, color: "rgba(255,255,255,0.8)" }}>
            {level.next - total} pts para {getUserLevel(level.next).tier}
          </small>
        </>
      )}
      <div className={styles.pointsBottom} style={{ marginTop: 8 }}>
        <FontAwesomeIcon icon={faArrowTrendUp} />
        +{today} hoje
      </div>
    </div>
  );
}

// ── ProgressBar ───────────────────────────────────────────────────────────────
export function ProgressBar({ percent, variant = "points" }) {
  const clamped  = Math.max(0, Math.min(100, percent));
  const barClass = variant === "time" ? styles.barTime : styles.barPoints;
  return (
    <div className={styles.barTrack}>
      <div className={cn(styles.barFill, barClass)} style={{ width: `${clamped}%` }} />
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────
export function Modal({
  children,
  onClose,
  onConfirm,
  confirmLabel = "Salvar",
  confirmDisabled = false,
  isMobile,
  title,
  hideCancelBtn = false,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  useBodyScrollLock();

  const handleConfirm = async () => {
    if (isSubmitting || confirmDisabled) return;
    try {
      setIsSubmitting(true);
      await onConfirm?.();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose?.();
  };

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div
        className={cn(styles.modalShell, isMobile && styles.modalMobile)}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader onClose={handleClose} disabled={isSubmitting} />

        <div className={styles.modalBody}>{children}</div>

        <div className={cn(styles.modalFooter, isMobile && styles.modalFooterMobile)}>
          {!hideCancelBtn && (
            <button
              className={cn(styles.btn, styles.btnSecondary)}
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancelar
            </button>
          )}
          <button
            className={cn(styles.btn, styles.btnPrimary)}
            onClick={handleConfirm}
            disabled={confirmDisabled || isSubmitting}
          >
            {isSubmitting ? (
              <span className={styles.saving}>
                <span className={styles.spinner} />
                Salvando...
              </span>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
